import { EditOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Checkbox, Col, Divider, Row, Spin,
  Switch,
  Tag,
  Typography
} from 'antd';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import getDropdownTypeService from 'common/services/dropdownType';
import { getPackageDetailService } from 'common/services/packages';
import { ServiceAddOnPackageData } from 'common/services/packages/types';
import { KNOWLEDGE_SOURCES, ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';
import { packageDetailSchema } from 'common/utils/schemas';
import { packageDurations } from 'extends/PackageDetail';
import PackageTierConfig from 'extends/PackageDetail/components/PackageTierConfig';

export type FormTierValues = {
  index: number;
  name: string;
  numberOfAgent: number;
  knowledges: boolean[];
  knowledgeLimits: {
    numberOfFile: number;
    fileSizeLimit: number;
    totalSizeLimit: number;
    numberOfWeb: number;
  };
  customServices: {
    [key: string]: boolean;
  };
  endUserSides: {
    numberOfSession: number;
    chatUpload: boolean;
    chatExport: boolean;
    agentTypeBoth: boolean;
    s2t: boolean;
    t2s: boolean;
  },
  price: number;
};

export type FormValues = {
  name: string;
  basePrice: number;
  duration: { value: number; label: string };
  isApplyPayg: boolean;
  tiers: FormTierValues[];
  servicePackageAddOnsData?: ServiceAddOnPackageData[]
};

const PackageDetailView = () => {
  /* Hook */
  const { t } = useTranslation();
  const method = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      basePrice: 0,
      duration: undefined,
      isApplyPayg: false,
      tiers: [],
      servicePackageAddOnsData: []
    },
    resolver: yupResolver(packageDetailSchema),
  });
  const navigate = useNavigate();

  /* Selectors */

  /* Search Params */
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));

  /* States */

  /* React-query */
  const { data: packageData, isLoading: isPackageDataLoading } = useQuery(
    ['getPackageDetail'],
    () => getPackageDetailService(idParams),
    {
      onSuccess: (data) => {
        const formattedTiers = data.servicePackageData.tiers?.map((tier) => ({
          ...tier,
          knowledges:
            Array.from(
              { length: Object.entries(KNOWLEDGE_SOURCES).length + 1 },
              (_, i) => (tier.knowledges?.includes(i) ? true : undefined)
            ),
          customServices: Object.fromEntries(
            (tier.customServices || []).map((key) => [key, true])
          )
        }));

        method.reset({
          name: data.servicePackageData.name,
          basePrice: data.servicePackageData.basePrice,
          isApplyPayg: data.servicePackageData.isApplyPayg,
          tiers: formattedTiers,
          servicePackageAddOnsData: data.servicePackageData.servicePackageAddOnData
        });
      },
      enabled: !!searchParams.get('id'),
    }
  );

  const { data: customServices, isLoading: isCustomServicesLoading } = useQuery(
    ['getAllCustomServices'],
    () => getDropdownTypeService<any>('ai-assistant-service', {
      page: 1,
      limit: 200,
    }),
  );

  /* Functions */

  if (isPackageDataLoading) {
    return <Spin />;
  }

  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.packageDetail')}
        rightHeader={(
          <Button
            type="primary"
            loading={isPackageDataLoading || isCustomServicesLoading}
            onClick={() => { navigate(`${ROUTE_PATHS.PACKAGES_DETAIL}?id=${idParams}`); }}
          >
            <EditOutlined />
            {t('media.edit')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <div className="u-mt-16 p-packageDetail_actions">
          <Typography.Title level={2}>{t('package.generalInformation')}</Typography.Title>
          <Row>
            <Col span={12}>
              <table className="t-package_detail-right">
                <tr>
                  <th>
                    {t('package.nameServicePackage')}
                    :
                  </th>
                  <td>
                    {packageData?.servicePackageData.name}
                  </td>
                </tr>
                <tr>
                  <th>
                    {t('package.duration')}
                    :
                  </th>
                  <td>
                    {packageDurations(t)?.find((item) => item.value === packageData?.servicePackageData.duration)?.label || ''}
                  </td>
                </tr>
              </table>
              <div>
                <Checkbox
                  name="isApplyPayg"
                  checked={packageData?.servicePackageData.isApplyPayg}
                >
                  <Typography.Text>
                    {t('package.applyPAYG')}
                  </Typography.Text>
                </Checkbox>
              </div>
            </Col>
            <Col span={12}>
              <table className="t-package_detail-right">
                <tr>
                  <th>
                    {t('package.createdBy')}
                    :
                  </th>
                  <td>
                    {packageData?.creator.name}
                    {' '}
                    -
                    {' '}
                    {formatDateTime(packageData?.servicePackageData.createdAt)}
                  </td>
                </tr>
                <tr>
                  <th>
                    {t('package.lastModifiedBy')}
                    :
                  </th>
                  <td>
                    {packageData?.updater.name}
                    {' '}
                    -
                    {' '}
                    {formatDateTime(packageData?.servicePackageData.updatedAt)}
                  </td>
                </tr>
                <tr>
                  <th>
                    {t('system.status')}
                    :
                  </th>
                  <td><Switch checked={packageData?.servicePackageData.status === 2} /></td>
                </tr>
              </table>
            </Col>
          </Row>
          <Row>
            <Typography.Text strong>{t('sidebar.add_on_package')}</Typography.Text>
            <div style={{ width: '100%', marginTop: 8 }}>
              {packageData?.servicePackageData.servicePackageAddOnData ? (
                packageData.servicePackageData.servicePackageAddOnData.map((item) => (
                  <Tag key={item.id} color="green" style={{ padding: '6px 8px' }}>
                    {item.name}
                  </Tag>
                ))
              ) : (
                <Typography.Text type="secondary">{t('packageCompare.noData')}</Typography.Text>
              )}
            </div>
          </Row>

          <Divider />
          <Typography.Title level={2}>{t('package.tierConfig')}</Typography.Title>
          <PackageTierConfig method={method} customServicesData={customServices?.data} isDisplay />
        </div>
      </div>
    </>
  );
};
export default PackageDetailView;
